/**
 * QR-2, веб-срез В2: ОБЩАЯ КОМНАТА в кабинете владельца. Маршрут /msg/room.
 *
 * Зачем она (слова владельца 01.09, сразу после выкатки): «скинул одну ссылку —
 * получил два разных чата». Личная переписка 1:1 остаётся, комната — про
 * разговор всех сразу.
 *
 * Правила страницы (те же, что у чата 1:1, и по тем же причинам):
 * - гейт: `messengerV2 && msgRooms` из /me. При выключенной комнате вся
 *   поверхность /msg/rooms отдаёт голый 404, по которому «выключено»
 *   неотличимо от «сломано» — поэтому источник гейта именно /me;
 * - лидерство Web Locks ДО подъёма крипты, через общий `useMsgLeadership`:
 *   замок один на origin, и комната не имеет права поднять вторую машину
 *   на том же IndexedDB-сторе;
 * - криптослой тянется лениво внутри контроллера (WASM-чанк только здесь);
 * - страница живёт в `CabinetShell` (WEB-10): бургер, шапка, тумблер темы.
 *
 * 🔴 Чего здесь НЕТ и почему: доверие показывается ПО КАЖДОМУ участнику
 * отдельно (`trustByPrincipal`), а не одним баннером на комнату — общий
 * баннер соврал бы про тех, чей ключ не менялся.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import CabinetShell from '../../components/UI/CabinetShell/cabinetShell';
import { useAuth } from '../../context/AuthContext';
import { MsgApi } from '../../msg/msgApi';
import { browserDeviceId, rotateBrowserDeviceId } from '../../msg/deviceId';
import { forgetDeviceStore } from '../../msg/deviceTakeover';
import { storeNameFor } from '../../msg/chatController';
import { canAskYield } from '../../msg/leader';
import { useMsgLeadership } from '../../msg/useMsgLeadership';
import { RoomController } from '../../msg/roomController';
import { principalKey } from '../../msg/deviceFanout';
import { knownSenderName, senderName } from '../../msg/roomNames';
import { replySnapshot } from '../../msg/replyTo';
import {
  indexMessages, ReplyButton, ReplyDraftBar, ReplyQuote, useQuoteJump,
} from '../../msg/replyToUi';
import PinnedWarnings, { SendBlocked } from '../../msg/pinnedWarnings';
import SendButton from '../../msg/sendButton';
import SecurityPanel, { SecurityLock, unverifiedCount } from '../../msg/securityPanel';
import { AttachmentDraft, MsgAttachments, UploadingNote } from '../../msg/attachmentsUi';
import './msgChat.css';
import './roomChat.css';

const RoomChat = () => {
  const {
    id: ownerId, messengerV2, msgRooms, loaded,
  } = useAuth();
  const roomEnabled = messengerV2 && msgRooms;
  const [room, setRoom] = useState(null);
  // WEB-23: панель «Защита переписки» (закрыта по умолчанию) и своё
  // устройство — спрашиваем только когда её открыли.
  const [securityOpen, setSecurityOpen] = useState(false);
  const [myDevice, setMyDevice] = useState(null);
  // Фокус возвращается на замок при закрытии — иначе он падает на `body`.
  const securityLockRef = useRef(null);
  const closeSecurity = () => {
    setSecurityOpen(false);
    securityLockRef.current?.focus();
  };
  const [input, setInput] = useState('');
  // ATT С3: черновик-файл — как в 1:1 (C2a)
  const [file, setFile] = useState(null);
  // WEB-16: на какое сообщение отвечаем в комнате.
  const [replyTo, setReplyTo] = useState(null);
  const fileRef = useRef(null);
  // WEB-31: фокус возвращается в поле после отправки кнопкой
  const inputRef = useRef(null);
  // Переименование: форма открывается по кнопке и живёт РЯДОМ с чатом, не
  // вместо него — человек видит переписку, ради которой всё и затевалось.
  const [renaming, setRenaming] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [nameDraft, setNameDraft] = useState('');
  const [renameError, setRenameError] = useState(null);
  const [saving, setSaving] = useState(false);
  const listRef = useRef(null);
  // WEB-16 срез 4: переход к оригиналу по нажатию на цитату.
  const { jumpTo, highlighted } = useQuoteJump(listRef);
  // Фокус: при открытии — в первое поле, при закрытии — обратно на кнопку.
  // 🔴 Без этого фокус остаётся на кнопке, которая тут же размонтируется, и
  // у клавиатуры он уезжает на body: до формы приходится добираться табом с
  // начала страницы (ревью Codex р.1, Low).
  const titleRef = useRef(null);
  const openRef = useRef(null);
  const returnFocus = useRef(false);
  // Д-20: одноразовое разрешение вытеснить чужое устройство (см. msgChat).
  const takeDeviceRef = useRef(false);
  // Второй тап по «Перенести сюда», пока поднимается новый контроллер.
  const takingDeviceRef = useRef(false);

  const {
    blocked, yielded, taking, takeFailed, takeover, retryNow, controllerRef,
  } = useMsgLeadership({
    enabled: Boolean(loaded && roomEnabled && ownerId),
    deps: [ownerId],
    startController: () => {
      const did = browserDeviceId();
      const allowTakeover = takeDeviceRef.current;
      takeDeviceRef.current = false;
      takingDeviceRef.current = false;
      const controller = new RoomController({
        api: new MsgApi(did), ownerId, did, onUpdate: setRoom, allowTakeover,
      });
      controller.start();
      return controller;
    },
  });

  /**
   * Д-20: «Перенести сюда». Сначала РОТИРУЕМ did — прежний мог быть уже
   * ревокнут, а ревокнутый did выжжен навсегда (сервер ответит 403, и кнопка
   * бы не работала). Новый did = новый стор: переписка в этом браузере
   * начинается с чистого листа, о чём сказано в тексте вопроса.
   */
  const takeDevice = () => {
    // 🔴 Второе нажатие игнорируем (ревью 03.09): `retryNow` комнаты не
    // сбрасывает снимок `room`, поэтому кнопка живёт на экране всё время,
    // пока останавливается прежний контроллер и идёт эстафета лидерства.
    // Двойной тап дал бы ДВЕ ротации, и контроллер с did-2 ревокнул бы уже
    // записанный did-3 — человек получил бы 403 и ещё один круг.
    if (takingDeviceRef.current) return;
    takingDeviceRef.current = true;
    const old = browserDeviceId();
    rotateBrowserDeviceId();
    // Старый стор больше не нужен: did выжжен либо не регистрировался.
    forgetDeviceStore(storeNameFor({ type: 'owner', id: ownerId }, old));
    // 🔴 WEB-23: снимок своего устройства протух — did другой (ревью 03.09).
    setMyDevice(null);
    takeDeviceRef.current = true;
    retryNow();
  };

  useEffect(() => {
    listRef.current?.scrollTo(0, listRef.current.scrollHeight);
  }, [room?.messages]);

  useEffect(() => {
    if (renaming) { titleRef.current?.focus(); return; }
    // Возврат только после НАШЕГО закрытия формы, а не на каждом рендере без
    // неё: иначе страница отбирала бы фокус у того, кто её просто листает.
    if (returnFocus.current) {
      returnFocus.current = false;
      openRef.current?.focus();
    }
  }, [renaming]);

  // Запрет отправки (решение 07.09): ключ получателя сменился, человек ещё
  // не решил. Правило считает КОНТРОЛЛЕР (одно с составом доставки), страница
  // только читает.
  const sendStopped = Boolean(room?.sendBlocked);

  // Оригиналы для цитат — по ленте ЭТОЙ комнаты.
  const byId = indexMessages(room?.messages);

  const send = () => {
    const text = input.trim();
    const draft = file;
    // 🔴 При запрете НЕ очищаем поле: клик сюда не проходит (кнопка погашена,
    // Enter отсечён), но и стечься сюда из устаревшего замыкания текст не
    // должен — молчаливая очистка съела бы его.
    // 🔴 `uploading` проверяется ЗДЕСЬ, а не только погашенной кнопкой: Enter
    // идёт мимо неё, а загрузка — вне очереди контроллера; текст обгонял бы
    // сообщение с файлом (урок C2a р.5).
    if (sendStopped || Boolean(room?.uploading)
        || (!text && !draft) || !controllerRef.current) return;
    const quote = replyTo;
    setInput('');
    setFile(null);
    setReplyTo(null);
    // 🔴 Отказ контроллера (`false`) — черновик (текст И файл) возвращается,
    // если человек не начал собирать новый (контракт C2a; для комнаты это
    // закрытие WEB-20 — раньше успех и провал были неотличимы).
    Promise.resolve(controllerRef.current.send(text, draft ? [draft] : [], quote))
      .then((sent) => {
        if (sent === false) {
          setInput((cur) => cur || text);
          if (draft) setFile((cur) => cur || draft);
          if (quote) setReplyTo((cur) => cur || quote);
        }
      })
      .catch(() => {});
  };

  /** Как владелец подписан В КОМНАТЕ: это ростер, а не логин кабинета. */
  const myName = () => room?.membersByPrincipal?.[room?.self?.key]?.displayName ?? '';

  const closeRename = () => {
    returnFocus.current = true;
    setRenaming(false);
  };

  const openRename = () => {
    setTitleDraft(room?.room?.title ?? '');
    setNameDraft(myName());
    setRenameError(null);
    setRenaming(true);
  };

  /**
   * Отказ сервера человеческими словами. 🔴 Сырой текст ошибки сюда не идёт:
   * «HTTP 422 …» человеку не говорит ни что случилось, ни что делать.
   */
  const renameErrorText = (e) => {
    if (e?.status === 403) return 'Переименовать может только тот, кто завёл чат.';
    if (e?.status === 404) return 'Чат не найден — обновите страницу.';
    if (e?.status === 422) return 'Название и подпись — от 1 до 64 символов, одной строкой.';
    return 'Не удалось переименовать — попробуйте ещё раз.';
  };

  const submitRename = async (e) => {
    e.preventDefault();
    const title = titleDraft.trim();
    const displayName = nameDraft.trim();
    if (!title || !displayName) {
      setRenameError('Название и подпись не могут быть пустыми.');
      return;
    }
    // 🔴 Шлём ТОЛЬКО изменённое: сервер различает «поле не пришло» и «пришло»,
    // и отправка неизменного поля переписывала бы его само на себя — лишняя
    // запись под локом комнаты на каждое нажатие «Сохранить».
    const patch = {};
    if (title !== (room?.room?.title ?? '')) patch.title = title;
    if (displayName !== myName()) patch.displayName = displayName;
    if (!Object.keys(patch).length) { closeRename(); return; }
    setSaving(true);
    setRenameError(null);
    try {
      await controllerRef.current?.rename(patch);
      closeRename();
    } catch (err) {
      setRenameError(renameErrorText(err));
    } finally {
      setSaving(false);
    }
  };

  /**
   * Форма переименования. Два поля рядом намеренно: имя разговора и своя
   * подпись в нём приехали из ОДНОЙ подписи приглашения, и человек, который
   * пришёл чинить одно, почти всегда хочет починить и второе.
   */
  const renameForm = () => (
    <form className="roomRenameForm" data-testid="room-rename-form" onSubmit={submitRename}>
      <label htmlFor="roomTitleInput">
        Название чата
        <input
          id="roomTitleInput"
          ref={titleRef}
          data-testid="room-rename-title"
          value={titleDraft}
          maxLength={64}
          onChange={(e) => setTitleDraft(e.target.value)}
        />
      </label>
      <label htmlFor="roomNameInput">
        Ваша подпись в этом чате
        <input
          id="roomNameInput"
          data-testid="room-rename-name"
          value={nameDraft}
          maxLength={64}
          onChange={(e) => setNameDraft(e.target.value)}
        />
      </label>
      <p className="roomRenameNote">
        Коллеги увидят новое название, когда откроют чат в следующий раз.
      </p>
      {renameError && (
        <p className="msgChatError" data-testid="room-rename-error">{renameError}</p>
      )}
      <div className="roomRenameButtons">
        <button type="submit" disabled={saving} data-testid="room-rename-save">
          {saving ? 'Сохраняем…' : 'Сохранить'}
        </button>
        <button
          type="button"
          disabled={saving}
          data-testid="room-rename-cancel"
          onClick={closeRename}
        >
          Отмена
        </button>
      </div>
    </form>
  );

  /** «Кто здесь» — ростер комнаты. Владелец помечен, чтобы себя не искать. */
  const roster = () => {
    const members = Object.values(room?.membersByPrincipal ?? {});
    if (!members.length) return null;
    return (
      <details className="roomRoster" data-testid="room-roster">
        <summary>
          В комнате:
          {' '}
          {members.length}
        </summary>
        <ul>
          {members.map((m) => (
            <li key={principalKey(m.principalType, m.principalId)}>
              {m.displayName}
              {m.isOwner && <span className="roomYou"> — это вы</span>}
            </li>
          ))}
        </ul>
      </details>
    );
  };

  /**
   * Участники, которые ещё НЕ открыли чат: устройства у них нет, копию везти
   * некуда, и написанного сейчас они не увидят никогда. Называем поимённо —
   * молчание здесь дороже места на экране.
   */
  const silent = () => {
    const names = (room?.membersWithoutDevices ?? [])
      .map((p) => senderName(room, p.principalType, p.principalId));
    if (!names.length) return null;
    return (
      <p className="roomWarn" data-testid="room-silent">
        {names.length === 1
          ? `${names[0]} ещё не открыл чат и сообщение не получит.`
          : `Ещё не открыли чат и сообщение не получат: ${names.join(', ')}.`}
      </p>
    );
  };

  /** Смена ключа — по КАЖДОМУ участнику отдельно (§6 дизайна). */
  // Сводка для закреплённой строки: сколько устройств не проверено и есть ли
  // среди них сменившее ключ. Считается по тому же источнику, что и плашки, —
  // иначе строка и плашки разошлись бы во мнениях.
  const trustStates = Object.values(room?.trustByPrincipal ?? {});
  const changedCount = trustStates.filter((t) => t?.status === 'key_changed').length;

  // WEB-23: список для панели — ВСЕ, о ком контроллер что-то знает, включая
  // уже сверенных: панель отвечает на вопрос «что у нас с защитой», и
  // «проверено» — такой же ответ, как «нет».
  // 🔴 Только при ЖИВОЙ комнате (ревью 03.09, Medium): замок и панель висят
  // снаружи `body()`, а снимок `room` после уступки вкладки не обнуляется.
  // Иначе в шапке горела бы цифра, а в панели стояла бы кнопка «Код сверен»,
  // зовущая `controllerRef.current`, который уже `null`.
  const roomLive = room?.phase === 'ready' && !yielded && !blocked;
  const securityPeople = roomLive
    ? Object.entries(room?.trustByPrincipal ?? {}).map(([key, t]) => {
      const [type, id] = key.split(':');
      return { key, name: senderName(room, type, Number(id)), status: t?.status };
    })
    : [];

  // WEB-23: своё устройство для панели (Д-20 §3). Отказ ручки панель не
  // ломает — раздел просто не появляется.
  useEffect(() => {
    // 🔴 `|| myDevice` — СТОП-КРАН, а не оптимизация: `myDevice` в
    // зависимостях, и без него каждый успешный ответ перезаводил бы эффект
    // (ревью 03.09).
    if (!securityOpen || myDevice) return undefined;
    let alive = true;
    new MsgApi(browserDeviceId()).myDevice()
      .then((info) => { if (alive) setMyDevice(info ?? null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [securityOpen, myDevice]);

  // 🔴 В ленте остались ТОЛЬКО сменившиеся ключи. Первый контакт — состояние
  // по умолчанию, и именно он давал шесть плашек по 113px (замер 30.08).
  const trustBanners = () => Object.entries(room?.trustByPrincipal ?? {})
    .filter(([, t]) => t?.status === 'key_changed')
    .map(([key]) => {
      const [type, id] = key.split(':');
      const who = senderName(room, type, Number(id));
      return (
        <div
          key={key}
          className="msgTrustBanner msgTrustDanger"
          data-testid={`room-trust-${key}`}
        >
          <span>{`Ключ устройства ${who} СМЕНИЛСЯ. Сверьте код безопасности заново!`}</span>
          <button
            type="button"
            onClick={() => controllerRef.current?.markVerified(type, Number(id))}
          >
            Код сверен
          </button>
        </div>
      );
    });

  const body = () => {
    if (!loaded) return <p className="msgChatNote">Загрузка…</p>;
    if (!roomEnabled) return <p className="msgChatNote">Общий чат недоступен.</p>;
    if (taking) {
      return <p className="msgChatNote" data-testid="room-taking">Переносим чат сюда…</p>;
    }
    if (yielded) {
      return (
        <p className="msgChatNote" data-testid="room-yielded">
          Чат перенесён в другую вкладку — работайте там.
          {' '}
          <button type="button" onClick={takeover} disabled={taking}>Вернуть сюда</button>
        </p>
      );
    }
    if (blocked) {
      return (
        <p className="msgChatNote" data-testid="room-blocked">
          {takeFailed
            ? 'Не удалось перенести чат: та вкладка не ответила. Попробуйте ещё раз или закройте её.'
            : 'Чат открыт в другой вкладке этого браузера.'}
          {canAskYield() && (
            <>
              {' '}
              <button type="button" onClick={takeover} disabled={taking}>
                Открыть здесь
              </button>
            </>
          )}
          {!canAskYield() && ' Работайте там или закройте её и обновите эту.'}
        </p>
      );
    }
    if (!room || room.phase === 'init') {
      return <p className="msgChatNote">Подключение к общему чату…</p>;
    }
    // Д-20: не «Нет доступа к общему чату с этого устройства», а вопрос
    // с действием. Прежний текст прятал причину: устройство отключили,
    // когда чат открыли в другом месте.
    if (room.phase === 'error' && room.error?.kind === 'other_device') {
      return (
        <p className="msgChatNote" data-testid="room-other-device">
          {room.error.text}
          {' '}
          <button type="button" data-testid="room-other-device-take" onClick={takeDevice}>
            Перенести сюда
          </button>
        </p>
      );
    }
    // Д-3 «хвост»: крипто-стор погиб, did цел. Лечение у владельца одно —
    // новый did (та же ротация, что в Д-20). Без этой ветки экран упирался в
    // «Проверить сейчас», которая не могла сработать никогда: 409 на
    // регистрацию детерминирован, сколько ни проверяй.
    if (room.phase === 'error' && room.error?.kind === 'device_identity_lost') {
      return (
        <p className="msgChatNote msgChatError" data-testid="room-identity-lost">
          {room.error.text}
          {' Подключите этот браузер заново — переписка на экране останется,'
            + ' но сообщения, которые не успели прийти, не откроются.'}
          {/* 🔴 Ротация did меняет ключ устройства, значит у КАЖДОГО собеседника
              загорится «ключ сменился», а по правилу WEB-14 у него встанет
              отправка, пока он не нажмёт «Код сверен». Не сказать об этом —
              оставить человека гадать, почему ему перестали писать. */}
          {' Собеседникам придётся заново сверить код безопасности: до этого'
            + ' они не смогут вам писать.'}
          {' '}
          <button
            type="button"
            data-testid="room-identity-lost-rejoin"
            onClick={takeDevice}
          >
            Подключить заново
          </button>
        </p>
      );
    }
    if (room.phase === 'error' && !room.error?.retriable) {
      return (
        <p className="msgChatNote msgChatError" data-testid="room-error">
          {room.error?.text ?? 'Общий чат не поднялся.'}
          {' '}
          <button type="button" onClick={retryNow}>Проверить сейчас</button>
        </p>
      );
    }
    const messages = room.messages ?? [];
    return (
      <>
        {/* 🔴 WEB-23: `firstUse` сюда больше не передаётся — «первый контакт»
            переехал в панель «Защита переписки». Именно он и давал те самые
            шесть плашек по 113px: непроверенным устройство считается по
            умолчанию, а «Код сверен» в пилоте не нажимал никто. «Ключ
            СМЕНИЛСЯ» остаётся: на нём стоит запрет отправки WEB-14. */}
        <PinnedWarnings
          changed={changedCount}
          onShow={() => listRef.current?.scrollTo(0, 0)}
        />
        {/* 🔴 Прокручивается ВСЯ надстройка вместе с лентой, а не одна лента.
            Живой замер 30.08 на телефоне 360×720: в комнате с шестью
            непроверенными устройствами плашки доверия занимали 6×113 = 678px
            при области в 648 — больше всего экрана. Лента упиралась в свой пол
            96px, а поле ввода оказывалось на 1139-м пикселе, то есть комната
            открывалась «полной предупреждений и без переписки».
            🔴 Копия разметки в гейте этого показать не могла: в ней плашек
            было две. Нашлось только на настоящем приложении.
            Прижатым остаётся ввод — то, ради чего экран и открыт. */}
        <div
          className="msgScroll"
          ref={listRef}
          // 🔴 Прокручиваемая область обязана быть достижима с клавиатуры
          // (ревью Codex 30.08, Medium): без `tabIndex` до истории не
          // добраться ни стрелками, ни PageUp — Tab уходит сразу в ввод.
          tabIndex={0}
          role="region"
          aria-label="Переписка и предупреждения безопасности"
          data-testid="msg-scroll"
        >
          {trustBanners()}
          {roster()}
          {silent()}
          <ul className="msgList roomList">
          {messages.length === 0 && (
            <li className="roomEmpty" data-testid="room-empty">
              Здесь пока никто ничего не писал. Напишите первым — увидят все.
            </li>
          )}
          {messages.map((m) => (
            <li
              key={m.messageId}
              data-testid={`room-msg-${m.messageId}`}
              data-message-id={m.messageId}
              // tabIndex -1: строка не встаёт в обход по Tab, но МОЖЕТ принять
              // фокус при переходе к оригиналу (WEB-16 срез 4)
              tabIndex={-1}
              className={`msgItem ${m.direction === 'out' ? 'msgOut' : 'msgIn'} ${m.pending ? 'msgPending' : ''}`
                + `${highlighted === m.messageId ? ' msgItemFound' : ''}`}
            >
              {m.direction !== 'out' && (
                <span className="roomSender" data-testid="room-sender">
                  {senderName(room, m.senderType, m.senderId)}
                </span>
              )}
              {/* WEB-16: цитата НАД текстом. Оригинал — только из ЭТОЙ
                  комнаты, имя автора — из РОСТЕРА, а не из снимка
                  отправителя: в комнате приписать фразу можно любому. */}
              <ReplyQuote
                quote={m.replyTo}
                original={m.replyTo ? byId.get(m.replyTo.messageId) : null}
                names={{
                  authorOf: (orig) => knownSenderName(room, orig.senderType, orig.senderId),
                }}
                onJump={jumpTo}
              />
              <span className="msgText">
                {m.decryptFailed
                  ? 'Сообщение не удалось расшифровать'
                  : m.text}
              </span>
              <MsgAttachments
                attachments={m.attachments}
                fetchBlob={(meta) => controllerRef.current.downloadAttachment(meta)}
              />
              <span className="msgMeta">
                {m.pending ? 'отправляется…' : m.sentAt ? new Date(m.sentAt).toLocaleString() : ''}
                {!m.pending && !m.decryptFailed && m.direction !== 'system' && (
                  <ReplyButton
                    disabled={room.phase !== 'ready'}
                    onClick={() => {
                      const who = knownSenderName(room, m.senderType, m.senderId);
                      const snap = replySnapshot(m, who ?? '');
                      setReplyTo(snap && { ...snap, mine: m.direction === 'out' });
                    }}
                  />
                )}
              </span>
            </li>
          ))}
          </ul>
        </div>
        {room.error && (
          <div className="msgChatError msgSendError">
            <span>{room.error.text}</span>
            {room.error.retriable && (
              <button type="button" onClick={() => controllerRef.current?.retryPending()}>
                Повторить
              </button>
            )}
          </div>
        )}
        <SendBlocked
          blocked={room.sendBlocked}
          onSendAnyway={() => controllerRef.current?.sendAnyway()}
        />
        <AttachmentDraft file={file} onClear={() => setFile(null)} />
        <ReplyDraftBar quote={replyTo} onCancel={() => setReplyTo(null)} />
        <UploadingNote uploading={room.uploading} />
        <div className="msgInputRow">
          {/* ATT С3: тот же бокс, что в 1:1 после C2a — скрепка ВНУТРИ белого
              поля. Правила C2a скоуплены прямым ребёнком строки ровно ради
              этого переезда. */}
          <div className="msgInputBox" data-testid="input-box">
            <input
              type="text"
              ref={inputRef}
              value={input}
              placeholder="Сообщение всем…"
              disabled={room.phase !== 'ready'}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
            />
            <input
              type="file"
              ref={fileRef}
              hidden
              data-testid="attach-input"
              onChange={(e) => {
                const picked = e.target.files?.[0];
                if (picked) setFile(picked);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              className="msgAttachBtn"
              title="Прикрепить файл"
              aria-label="Прикрепить файл"
              disabled={room.phase !== 'ready' || Boolean(room.uploading)}
              onClick={() => fileRef.current?.click()}
            >
              📎
            </button>
            {/* WEB-31: отправка — ВНУТРИ бокса, справа от скрепки. Появляется,
                когда есть что отправлять; при запрете отправки и во время
                загрузки видна и погашена (правило — в самом компоненте). */}
            <SendButton
              inputRef={inputRef}
              text={input}
              file={file}
              ready={room.phase === 'ready'}
              sendStopped={sendStopped}
              uploading={room.uploading}
              onSend={send}
            />
          </div>
        </div>
      </>
    );
  };

  return (
    <CabinetShell
      title={room?.room?.title ?? 'Общий чат'}
      floor={false}
      fill
      action={(
        <SecurityLock
          count={unverifiedCount(securityPeople)}
          expanded={securityOpen}
          lockRef={securityLockRef}
          onOpen={() => setSecurityOpen((v) => !v)}
        />
      )}
    >
      <div className="MsgChat">
        <div className="msgChatMain">
          {/* WEB-23: строка «🔒 E2E — сервер видит только запечатанные
              конверты» убрана — она стоила целой строки над лентой и
              пересказывала лендинг с памяткой. Замок переехал в шапку
              каркаса и стал входом в панель. */}
          {securityOpen && (
            <SecurityPanel
              people={securityPeople}
              myDevice={myDevice}
              onVerify={(key) => {
                const [type, id] = key.split(':');
                controllerRef.current?.markVerified(type, Number(id));
              }}
              onClose={closeSecurity}
            />
          )}
          <p className="roomHint">
            Пишут все и видят все.
            {' '}
            <Link to="/msg/invite">Пригласить ещё</Link>
            {/* 🔴 Именно `phase === 'ready'`, а не просто «комната есть»: имя
                комнаты приходит РАНЬШЕ ростера (_resolveRoom эмитит до
                _syncRoster), и в этом окне форма открылась бы с ПУСТОЙ
                подписью — человек увидел бы «не может быть пустым» на ровном
                месте. */}
            {room?.phase === 'ready' && !renaming && (
              <>
                {' · '}
                <button
                  type="button"
                  className="roomLinkButton"
                  ref={openRef}
                  data-testid="room-rename-open"
                  onClick={openRename}
                >
                  Переименовать
                </button>
              </>
            )}
          </p>
          {renaming && renameForm()}
          {body()}
        </div>
      </div>
    </CabinetShell>
  );
};

export default RoomChat;
